import { useCallback, useEffect, useRef, useState } from 'react';
import axiosConfig from '../config/axiosConfig';

// Récupération de la baseURL (ex: http://localhost:5000/api)
const BASE_URL = axiosConfig.defaults.baseURL || '';

// ─── Types d'events ───────────────────────────────────────────────────────────
export const EVENT_TYPES = {
  CLIENT_CHOICE:     'client_choice',
  CLIENT_INPUT:      'client_input',
  SELLER_ACTION:     'seller_action',
  PHASE_ADVANCE:     'phase_advance',
  SESSION_END:       'session_end',
  HEARTBEAT:         'heartbeat',
};

// ─── Hook principal ───────────────────────────────────────────────────────────
export function useCommercialStream({ token, role }) {
  const [connected, setConnected]     = useState(false);
  const [clientEvents, setClientEvents] = useState([]);
  const [lastEvent, setLastEvent]     = useState(null);
  const [error, setError]             = useState(null);

  const esRef       = useRef(null);
  const tokenRef    = useRef(token);
  const roleRef     = useRef(role);

  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { roleRef.current  = role;  }, [role]);

  // ── SSE : uniquement côté vendeur ──
  useEffect(() => {
    if (!token || role !== 'seller') return;

    const url = `${BASE_URL}/commercial/stream/${token}`;
    const es  = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      setError(null);
      console.log('[CommercialSSE] Connexion vendeur ouverte — token:', token);
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === EVENT_TYPES.HEARTBEAT) return;

        if (data.type === EVENT_TYPES.CLIENT_CHOICE || data.type === EVENT_TYPES.CLIENT_INPUT) {
          const enriched = { ...data, receivedAt: Date.now(), status: 'pending' };
          setClientEvents(prev => [...prev, enriched]);
          setLastEvent(enriched);
        }

        if (data.type === EVENT_TYPES.PHASE_ADVANCE) {
          setLastEvent({ ...data, receivedAt: Date.now() });
        }

        if (data.type === EVENT_TYPES.SESSION_END) {
          setConnected(false);
          es.close();
        }
      } catch (err) {
        console.error('[CommercialSSE] Erreur parse:', err);
      }
    };

    es.onerror = (err) => {
      console.error('[CommercialSSE] Erreur connexion:', err);
      setConnected(false);
      setError('Connexion SSE perdue — reconnexion automatique...');
    };

    return () => {
      es.close();
      esRef.current = null;
      console.log('[CommercialSSE] Fermeture connexion vendeur');
    };
  }, [token, role]);

  // ── Polling client (remplace fetch par axios) ──
  useEffect(() => {
    if (!token || role !== 'client') return;

    let active = true;
    let lastSnapshotStr = '';

    const poll = async () => {
      try {
        const res = await axiosConfig.get(`/commercial/session/${token}/state`);
        if (res.status === 200) {
          const data = res.data;
          const newSnapshotStr = JSON.stringify(data.currentSnapshot || {});
          if (newSnapshotStr !== lastSnapshotStr) {
            lastSnapshotStr = newSnapshotStr;
            setLastEvent({
              type: 'STATE_UPDATE',
              state: data.currentSnapshot || null,
              receivedAt: Date.now(),
            });
          }
          if (data.sessionEnded) {
            setLastEvent({ type: 'SESSION_END', receivedAt: Date.now() });
            active = false;
          }
        }
      } catch (_) {}
    };

    poll();
    const id = setInterval(() => { if (active) poll(); }, 2000);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [token, role]);

  // ── POST action client ─────────────────────────────────────────────────────
  const postAction = useCallback(async (event) => {
    if (!tokenRef.current) return { ok: false, error: 'No token' };

    const payload = {
      ...event,
      sentAt: Date.now(),
      role:   roleRef.current,
    };

    try {
      const res = await axiosConfig.post(`/commercial/action/${tokenRef.current}`, payload);
      return { ok: true, data: res.data };
    } catch (err) {
      console.error('[CommercialSSE] Erreur POST action:', err);
      return { ok: false, error: err.message };
    }
  }, []);

  // ── POST phase advance (vendeur → client sync) ─────────────────────────────
  const postPhaseAdvance = useCallback(async (phaseInfo) => {
    if (!tokenRef.current) return;
    try {
      await axiosConfig.post(`/commercial/action/${tokenRef.current}`, {
        type: EVENT_TYPES.PHASE_ADVANCE,
        ...phaseInfo,
        sentAt: Date.now(),
        role: 'seller',
      });
    } catch (err) {
      console.error('[CommercialSSE] Erreur postPhaseAdvance:', err);
    }
  }, []);

  // ── Clôturer la session ────────────────────────────────────────────────────
  const closeSession = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      await axiosConfig.delete(`/commercial/session/${tokenRef.current}`);
    } catch (err) {
      console.error('[CommercialSSE] Erreur closeSession:', err);
    }
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setConnected(false);
  }, []);

  // ── Marquer des events comme compilés ──────────────────────────────────────
  const markEventsCompiled = useCallback((eventIds) => {
    setClientEvents(prev =>
      prev.map(e => eventIds.includes(e.id) ? { ...e, status: 'compiled' } : e)
    );
  }, []);

  return {
    connected,
    clientEvents,
    lastEvent,
    error,
    postAction,
    postPhaseAdvance,
    closeSession,
    markEventsCompiled,
  };
}